import React from "react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { lifestyleFor } from "@/data/lifestyle";
import { Motif } from "@/lib/motifs";

/**
 * The Herbarium Album — each piece is taped into a designer's field album
 * like a botanical specimen. Slight per-item rotation, warm tape corners,
 * a hand-lettered index card next to it.
 */
const TILTS = [-3.5, 2.2, -1.4, 3.1, -2.6, 1.8];
const TAPES = [
  { top: -6, left: 16, rotate: -18 },
  { top: -8, right: 22, rotate: 14 },
];

const price = (n) => `₹${n.toLocaleString("en-IN")}`;

const CollectionHerbarium = ({ items = [] }) => {
  const six = items.slice(0, 6);
  return (
    <div className="relative">
      {/* subtle squared-paper wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "repeating-linear-gradient(90deg, rgba(200,180,150,0.06) 0px, rgba(200,180,150,0.06) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(0deg, rgba(200,180,150,0.06) 0px, rgba(200,180,150,0.06) 1px, transparent 1px, transparent 40px), radial-gradient(ellipse at center, rgba(232,223,209,0.5) 0%, rgba(246,241,233,0) 70%)",
        }}
      />
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 md:gap-x-24 gap-y-16 md:gap-y-24">
          {six.map((p, i) => {
            const tilt = TILTS[i % TILTS.length];
            const leftSide = i % 2 === 0;
            return (
              <Link
                key={p.slug}
                to={`/product/${p.slug}`}
                className="group grid grid-cols-5 gap-4 items-center"
                data-testid={`herbarium-item-${p.slug}`}
              >
                {/* photo — taped in, tilted */}
                <div
                  className={`col-span-3 relative ${leftSide ? "" : "md:order-2 md:col-start-3"}`}
                  style={{
                    transform: `rotate(${tilt}deg)`,
                    transformOrigin: "center",
                    transition: "transform 600ms ease",
                  }}
                >
                  <div className="relative group-hover:scale-[1.02] transition-transform duration-[600ms] ease-out">
                    <div
                      className="p-2 bg-ground"
                      style={{ boxShadow: "0 6px 22px -8px rgba(28,26,25,0.35)" }}
                    >
                      <img
                        src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
                        alt={p.name}
                        loading="lazy"
                        className="w-full aspect-[4/5] object-cover"
                        style={{ filter: "sepia(0.05) saturate(0.95)" }}
                      />
                    </div>
                    {TAPES.map((ts, ti) => (
                      <div
                        key={ti}
                        className="absolute"
                        style={{
                          top: ts.top,
                          left: ts.left,
                          right: ts.right,
                          width: 60,
                          height: 18,
                          background: "rgba(232,223,209,0.78)",
                          transform: `rotate(${ts.rotate}deg)`,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                </div>

                {/* index card */}
                <div className={`col-span-2 ${leftSide ? "" : "md:order-1"}`}>
                  <div style={{ transform: `rotate(${-tilt / 2}deg)` }}>
                    <div className="serif text-3xl md:text-4xl leading-tight">{p.name}</div>
                    <div className="flex items-center gap-3 mt-3 mb-4">
                      <Motif name={p.motif} size={22} strokeColor="var(--accent)" />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-ink/60">
                        {p.motif} · specimen 0{i + 1}
                      </span>
                    </div>
                    <div className="hairline-t hairline-b py-3 my-3">
                      <div className="flex justify-between text-xs text-ink/70">
                        <span>Age</span>
                        <span>{p.age_range || "—"}</span>
                      </div>
                      <div className="flex justify-between text-xs text-ink/70 mt-1">
                        <span>Collected</span>
                        <span>Winter · Bengaluru</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="nav-item">Price</span>
                        <span className="serif text-lg">{price(p.price)}</span>
                      </div>
                    </div>
                    <span className="crimson-link nav-item !text-[11px]">Open the entry →</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CollectionHerbarium;
